import { FC } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import styled, { useTheme } from 'styled-components'
import { message as AntMessage } from 'antd'

import { FullWideNotification } from 'src/Components/FullWideNotification'
import { CTAButton } from 'src/Components/Buttons/CTAButton'
import { sendQuickLoginLink } from 'src/Redux/auth-process/emailPasswordSlice/api-actions'
import { useAppDispatch } from 'src/Hooks/redux'
import { ApiError } from 'src/Redux/types'
import { useQuickLoginUTMParameters } from 'src/Hooks/useQuickLoginUTMParameters'
import { quickLogin } from 'src/Helpers/TagManager/AuthEvents'

const LoginLinkButton = styled(CTAButton)`
  width: 100%;
  background: ${({ theme }) => theme.color.mediumOrange};
  border-color: ${({ theme }) => theme.color.mediumOrange};
  color: ${({ theme }) => theme.unchangingColor.black};

  svg path {
    fill: ${({ theme }) => theme.unchangingColor.white};
  }

  &:hover,
  &:focus,
  &:focus-visible {
    background: ${({ theme }) => theme.color.mediumOrange};
    border-color: ${({ theme }) => theme.color.mediumOrange};
    color: ${({ theme }) => theme.unchangingColor.white};
    opacity: 0.9;
  }

  &:active {
    background: ${({ theme }) => theme.color.mediumOrange};
    border-color: ${({ theme }) => theme.color.mediumOrange};
    color: ${({ theme }) => theme.unchangingColor.white};
    opacity: 0.8;
  }
`
const DELAY = 300

export const SendLoginLink: FC<{ title?: string; email: string }> = ({
  email,
  title = 'Email me a quick login link',
}) => {
  const theme = useTheme()
  const [messageApi, contextHolder] = AntMessage.useMessage()
  const dispatch = useAppDispatch()
  const utmParameters = useQuickLoginUTMParameters()

  const handleSendQuickLink = (): void => {
    if (!email) {
      return
    }
    void dispatch(
      sendQuickLoginLink({
        email,
        utmParameters,
      }),
    )
      .unwrap()
      .then((res) => {
        void messageApi.open({
          content: (
            <FullWideNotification message='We`ve sent you an email with a link to access your account.' />
          ),
          duration: 3,
          style: { backgroundColor: theme.color.brandGreen },
          icon: null,
        })
        quickLogin()
        return res
      })
      .catch((err: ApiError) => {
        const { message } = err
        if (
          message === 'Curator in blacklist' ||
          message === 'Country in blacklist'
        ) {
          void messageApi.open({
            content: (
              <FullWideNotification message='Sorry, our platform is no longer available for users from your country' />
            ),
            duration: 3,
            style: { backgroundColor: theme.color.mediumRed },
            icon: null,
          })
        }
      })
  }

  const debouncedSendQuickLink = useDebouncedCallback(() => {
    handleSendQuickLink()
  }, DELAY)

  return (
    <>
      {contextHolder}
      <LoginLinkButton onClick={debouncedSendQuickLink}>
        {title}
      </LoginLinkButton>
    </>
  )
}
